import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/ToastProvider'
import { useDeleteAction } from '../../actions/hooks/useActions'
import type { MinuteItem, MinuteItemKind } from '../../minuteItems/types'
import { useDeleteMeetingItem } from './useDeleteMeetingItem'

/** Onay penceresi grupları — sayfadaki `<ConfirmDialog group=…>` bileşenleriyle eşleşir. */
export const ACTION_DELETE_GROUP = 'meeting-action-delete'
export const ITEM_DELETE_GROUP = 'item-delete'

interface Params {
  meetingId: number
  /**
   * Silme bu ekranda mümkün mü? (`RECORD_DELETE` yetkisi **ve** toplantı arşivli değil). `false` iken
   * geri çağrılar `undefined` döner → tablolar çöp kutusunu hiç çizmez.
   */
  enabled: boolean
}

/**
 * Toplantı Detay'daki **silme** davranışı (2026-09-11): görev ve madde (gündem/prensip/karar).
 *
 * <p><b>Neden ayrı bir hook:</b> iki mutasyon + iki onay penceresi + yetki/arşiv koşulu sayfanın gövdesine
 * yazıldığında o bileşenin bilişsel karmaşıklığı 9'dan **30**'a çıkmıştı (push kapısı ölçümü,
 * `eslint-plugin-sonarjs`; Sonar eşiği 15). Dallanma buraya taşındı; sayfa yalnız hazır iki geri çağrı
 * alır. Aynı desen: `useEditableMinuteItems`.
 *
 * <p>Silme <b>düzenleme yetkisinden bağımsızdır</b>: tutanağı düzenleyemeyen bir kullanıcı (ör. Genel
 * Müdür) de silebilir — o yüzden koşul `MINUTES_CREATE` değil, çağıranın verdiği {@link Params.enabled}.
 */
export function useMeetingRecordDelete({ meetingId, enabled }: Params) {
  const { t } = useTranslation()
  const toast = useToast()
  const qc = useQueryClient()
  const deleteAction = useDeleteAction()
  const deleteItem = useDeleteMeetingItem(meetingId)

  const refreshMeeting = () => {
    qc.invalidateQueries({ queryKey: ['meeting-detail', meetingId] })
    qc.invalidateQueries({ queryKey: ['meeting-logs', meetingId] })
  }

  /**
   * Görevi sil — soft-delete + alt görevler kademeli + denetim izi (513). Onay metni görevi **adıyla**
   * sorar: tabloda birden çok satır var ve yanlış çöp kutusuna basmak geri alınamaz.
   */
  const confirmActionDelete = (id: number, name: string) =>
    confirmDialog({
      group: ACTION_DELETE_GROUP,
      header: t('actions.detail.delete.action'),
      message: t('actions.detail.delete.confirmNamed', { name: name || '—' }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteAction.mutate(id, {
          onSuccess: () => {
            toast.success(t('actions.detail.delete.done'))
            refreshMeeting()
          },
          onError: () => toast.error(t('actions.detail.delete.error')),
        }),
    })

  /** Gündem/prensip/karar maddesini sil — soft-delete + denetim izi (530/520/525). */
  const confirmItemDelete = (item: MinuteItem, kind: MinuteItemKind) =>
    confirmDialog({
      group: ITEM_DELETE_GROUP,
      header: t('minuteItems.common.removeConfirm.title'),
      message: t('minuteItems.common.removeConfirm.message', { name: item.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteItem.mutate(item.id, {
          onSuccess: () =>
            toast.success(
              t('meetings.detail.saved.delete', { item: t(`meetings.detail.saved.item.${kind}`) }),
            ),
          onError: () => toast.error(t('meetings.detail.itemsSaveError')),
        }),
    })

  return {
    /** Görev tablosuna verilir; `undefined` iken çöp kutusu çizilmez. */
    onActionDelete: enabled
      ? (row: { id: number | null; name: string }) =>
          row.id != null && confirmActionDelete(row.id, row.name)
      : undefined,
    /** Madde listesine verilir (tip başına ayrı geri çağrı — başarı mesajı tipin adını yazar). */
    itemDeleteHandler: (kind: MinuteItemKind) =>
      enabled ? (item: MinuteItem) => confirmItemDelete(item, kind) : undefined,
  }
}
